import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Clapperboard, Loader2, Info, ImagePlus, Trash2, Bookmark, Check, Wand2, Globe } from 'lucide-react';
import { fileUrl, api } from '../../lib/api';
import { downscaleToDataUrl } from '../../lib/img';
import { toast } from '../../hooks/use-toast';
import { UGC_SCRIPTS } from './nodes';

// Tiers rather than model names. Which model actually renders depends on the
// tier, the length asked for and which provider has credit at that moment, so
// naming one here would be a promise the renderer cannot keep. It already broke
// once: the dialog advertised Veo while Kling made the clip. The model that did
// run is shown on the finished result instead, where it is a fact.
const TIERS = [
  { key: 'fast', label: 'Fast', note: 'quickest and cheapest · with audio' },
  { key: 'standard', label: 'Standard', note: 'balanced quality · with audio' },
  { key: 'premium', label: 'Premium', note: 'best quality · with audio' },
];

const ASPECTS = ['9:16', '16:9', '1:1'];

// Marketers think in platforms, not in aspect ratios. Several of these resolve
// to the same numbers, which is the honest answer: vertical short-form is one
// format wearing four logos.
const PLATFORMS = [
  { label: 'TikTok', aspect: '9:16', duration: 15 },
  { label: 'Reels', aspect: '9:16', duration: 15 },
  { label: 'Shorts', aspect: '9:16', duration: 15 },
  { label: 'IG feed', aspect: '1:1', duration: 10 },
  { label: 'YouTube', aspect: '16:9', duration: 25 },
];

export default function VideoDialog({ source, seed, onClose, onGenerate, busy }) {
  const [model, setModel] = useState('fast');
  const [duration, setDuration] = useState(8);
  const [aspect, setAspect] = useState('9:16');
  const [prompt, setPrompt] = useState('the model looks at camera and presents the product, casual handheld UGC selfie style, natural motion');
  const [reveal, setReveal] = useState(null);
  const revealRef = useRef(null);
  // What is being shown decides how it is composited in. One upload box that
  // meant "inside the box" whatever you gave it turned a website screenshot
  // into a sheet of paper lying in a cardboard box.
  const [revealMode, setRevealMode] = useState('none');
  const [revealUrl, setRevealUrl] = useState('');
  const [grabbing, setGrabbing] = useState(false);

  // The canvas already knows what this clip is meant to be. A template writes
  // its script, length and format onto the video node, and until now nothing
  // read them back, so the creator typed a script into the template and then
  // met the generic default here.
  //
  // Applied when the dialog opens rather than as initial state: this component
  // stays mounted and renders null while closed, so a useState initialiser
  // would only ever run once, at boot, before any node existed.
  const seededFor = useRef(null);
  useEffect(() => {
    if (!source || !seed) return;
    if (seededFor.current === source.id) return;  // do not undo their edits
    seededFor.current = source.id;
    if (seed.prompt) setPrompt(seed.prompt);
    if (seed.duration) setDuration(seed.duration);
    if (seed.aspect) setAspect(seed.aspect);
    if (seed.model) setModel(seed.model);
  }, [source, seed]);

  const grabSite = async () => {
    const url = revealUrl.trim();
    if (!url) return;
    setGrabbing(true);
    try {
      const shot = await api.screenshotWebsite({ url, mode: 'desktop' });
      const abs = fileUrl(shot.url || `/api/files/${shot.file_key}`);
      // Fetched back as a data URL because that is what the compositor takes,
      // and it keeps working if our own media is not reachable from outside.
      // downscaleToDataUrl reads a Blob. Handing it the data URL instead threw
      // on every capture, and the throw landed in the catch below, so the button
      // reported the page as unreachable when the page had been fetched fine.
      const blob = await (await fetch(abs)).blob();
      setReveal(await downscaleToDataUrl(blob, 1280));
      toast({ title: 'Website captured', description: 'It will be shown on the screen in the frame.' });
    } catch (e) {
      toast({ title: 'Could not capture that page', description: e.message });
    } finally { setGrabbing(false); }
  };

  // A creator's own saved prompts, kept per account so a script written once can
  // be reused across workflows instead of being retyped.
  const [active, setActive] = useState(null);
  useEffect(() => { api.activeProviders().then(setActive).catch(() => {}); }, []);

  const [saved, setSaved] = useState([]);
  const [naming, setNaming] = useState(false);
  const [presetName, setPresetName] = useState('');

  const loadPresets = useCallback(() => {
    api.listPresets().then((r) => setSaved(r.filter((p) => p.kind === 'video'))).catch(() => {});
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  // The stock scripts are two generic lines. This asks a writing model for one
  // grounded in the scene the still was actually compiled from.
  const [drafting, setDrafting] = useState(false);
  const [scriptLang, setScriptLang] = useState('Spanish');
  const draftScript = async () => {
    if (!source?.id) { toast({ title: 'No source image to write against' }); return; }
    setDrafting(true);
    try {
      // What is already in the box is the brief. Writing over it without
      // sending it threw away the only statement of what the clip was for.
      const r = await api.draftMotionScript({
        generationId: source.id, seconds: duration, language: scriptLang, prompt,
      });
      setPrompt(r.script);
      toast({ title: 'Script drafted', description: 'Timed beats you can edit before generating.' });
    } catch (e) {
      toast({ title: 'Could not draft a script', description: e.message });
    } finally { setDrafting(false); }
  };

  // A content refusal arrives after the provider has already taken the credits,
  // so the only place a warning is worth anything is here, while the script is
  // still being written. The check is local to our own server, no model call,
  // so it can run on every pause in typing.
  const [findings, setFindings] = useState([]);
  const [softening, setSoftening] = useState(false);
  useEffect(() => {
    const text = prompt.trim();
    if (!text) { setFindings([]); return undefined; }
    const t = setTimeout(() => {
      api.checkScript({ script: text, seconds: duration })
        .then((r) => setFindings(r.findings || []))
        .catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [prompt, duration]);

  // Past ten seconds the clip is several joined, so it has several scripts. The
  // split used to happen inside the render where nobody could see it: getting
  // clip two wrong was only visible in the finished video, after paying for it.
  const [clips, setClips] = useState([]);
  const [edited, setEdited] = useState(false);
  const sequenced = duration > 10;
  useEffect(() => {
    if (!sequenced) { setClips([]); setEdited(false); return undefined; }
    if (edited) return undefined; // never overwrite what the creator has typed
    const t = setTimeout(() => {
      api.planSequence({ script: prompt, seconds: duration })
        .then((r) => setClips(r.clips || []))
        .catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [prompt, duration, sequenced, edited]);

  // The narration is ours only on a sequence, and it defaulted to a voice with
  // no accent instruction, so Spanish came out Latin American. The language
  // picked for the script drives the accent unless a voice is chosen here.
  // Unlabelled voice names put a woman's voice over a man on screen, because
  // nothing in the list said which was which and the default happened to be
  // female. The gender is the one thing you need to know to pick.
  const VO_VOICES = [
    { id: 'echo', label: 'Echo · male' },
    { id: 'onyx', label: 'Onyx · male, deep' },
    { id: 'fable', label: 'Fable · male' },
    { id: 'alloy', label: 'Alloy · neutral' },
    { id: 'nova', label: 'Nova · female' },
    { id: 'shimmer', label: 'Shimmer · female' },
  ];
  const [voVoice, setVoVoice] = useState('echo');

  const setClipScript = (i, text) => {
    setEdited(true);
    setClips((prev) => prev.map((c, n) => (n === i ? { ...c, script: text } : c)));
  };

  const soften = async () => {
    setSoftening(true);
    try {
      const r = await api.softenScript({ script: prompt, language: scriptLang, seconds: duration });
      setPrompt(r.script);
      const left = (r.after || []).filter((f) => f.level === 'risk').length;
      toast({
        title: 'Script rewritten',
        description: left
          ? `${left} thing${left === 1 ? '' : 's'} still worth a look.`
          : 'Nothing left that we know trips the filters.',
      });
    } catch (e) {
      toast({ title: 'Could not rewrite it', description: e.message });
    } finally { setSoftening(false); }
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name || !prompt.trim()) { toast({ title: 'A preset needs a name and a script' }); return; }
    try {
      await api.createPreset({ name, text: prompt.trim(), kind: 'video' });
      toast({ title: 'Preset saved', description: `"${name}" is now in your library.` });
      setNaming(false); setPresetName(''); loadPresets();
    } catch (e) { toast({ title: 'Could not save', description: e.message }); }
  };

  const removePreset = async (p) => {
    try { await api.deletePreset(p.id); loadPresets(); }
    catch (e) { toast({ title: 'Could not delete', description: e.message }); }
  };

  if (!source) return null;

  const onRevealPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setReveal(await downscaleToDataUrl(f)); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#141417]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2A2A30] px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Clapperboard size={16} className="text-node-model" />
            <span className="font-medium text-white">Create UGC video</span>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-white/50 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>

        <div className="grid gap-4 p-5 sm:grid-cols-[120px_1fr]">
          <div className="overflow-hidden rounded-lg border border-[#2A2A30] bg-[#0E0E10]">
            <img src={fileUrl(source.url)} alt="" className="h-full w-full object-cover" />
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Quality</label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {TIERS.map((m) => (
                  <button key={m.key} onClick={() => setModel(m.key)} data-testid={`tier-${m.key}`}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${model === m.key ? 'border-node-model bg-node-model/10' : 'border-[#2A2A30] hover:border-white/25'}`}>
                    <p className="text-sm font-medium text-white">{m.label}</p>
                    <p className="text-[10px] leading-tight text-white/50">{m.note}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Target platform</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {PLATFORMS.map((p) => {
                  const on = aspect === p.aspect && duration === p.duration;
                  return (
                    <button key={p.label} data-testid={`platform-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => { setAspect(p.aspect); setDuration(p.duration); }}
                      className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${on ? 'border-node-model bg-node-model/15 text-white' : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Duration · {duration}s</label>
                <input type="range" min={4} max={25} step={1} value={duration} onChange={(e) => setDuration(parseInt(e.target.value))} className="mt-2 w-full accent-node-model" />
                <p className="mt-1 text-[10px] leading-relaxed text-white/35">
                  Past 10s the clip is built from several shorter ones joined together, each
                  from a different angle. They are rendered silent and one voice is laid over
                  the whole thing, so it does not change partway through.
                </p>
              </div>
              <div>
                <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Aspect</label>
                <div className="mt-1.5 flex gap-1.5">
                  {ASPECTS.map((a) => (
                    <button key={a} onClick={() => setAspect(a)} className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${aspect === a ? 'border-white bg-white text-black' : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>{a}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 pb-2">
          {/* Eighteen chips wrapped over four rows and pushed everything that
              matters below the fold, for a list you read once and then use the
              writer instead. One line, same content. */}
          <div className="flex items-center gap-2">
            <label className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-white/50">Start from</label>
            <select data-testid="ugc-script-pick" defaultValue=""
              onChange={(e) => { const s = UGC_SCRIPTS.find((x) => x.key === e.target.value); if (s) setPrompt(s.text); }}
              className="min-w-0 flex-1 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2 py-1 text-[11px] text-white/80 focus:border-node-model focus:outline-none">
              <option value="">a blank script</option>
              {UGC_SCRIPTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          {saved.length > 0 && (
            <>
              <label className="mt-2 block text-[11px] font-medium uppercase tracking-wider text-white/50">Your presets</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {saved.map((p) => (
                  <span key={p.id} data-testid={`preset-${p.id}`}
                    className="group inline-flex items-center gap-1 rounded-full border border-node-style/40 bg-node-style/10 px-2.5 py-1 text-[11px] text-white/80">
                    <button onClick={() => setPrompt(p.text)} title={p.text} className="hover:text-white">{p.name}</button>
                    <button onClick={() => removePreset(p)} title="Delete preset"
                      className="text-white/30 transition-colors hover:text-red-400"><Trash2 size={11} /></button>
                  </span>
                ))}
              </div>
            </>
          )}

          <div className="mt-2">
            {naming ? (
              <div className="flex items-center gap-1.5">
                <input autoFocus value={presetName} onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') savePreset(); if (e.key === 'Escape') setNaming(false); }}
                  placeholder="Name this preset" data-testid="preset-name"
                  className="w-48 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2 py-1 text-[11px] text-white focus:border-node-style focus:outline-none" />
                <button onClick={savePreset} data-testid="preset-save"
                  className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-black"><Check size={11} /> Save</button>
                <button onClick={() => setNaming(false)} className="text-[11px] text-white/40 hover:text-white">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setNaming(true)} data-testid="preset-add"
                className="inline-flex items-center gap-1 text-[11px] text-white/50 transition-colors hover:text-white">
                <Bookmark size={11} /> Save this script as a preset
              </button>
            )}
          </div>
        </div>

        <div className="px-5 pb-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Motion / script</label>
            <div className="flex items-center gap-1.5">
              <select value={scriptLang} onChange={(e) => setScriptLang(e.target.value)} data-testid="script-lang"
                className="rounded-md border border-[#2A2A30] bg-[#0E0E10] px-1.5 py-1 text-[11px] text-white/70 focus:border-node-style focus:outline-none">
                {['Spanish', 'English', 'Portuguese', 'French', 'German', 'Italian'].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            <button onClick={draftScript} disabled={drafting} data-testid="draft-script"
              className="inline-flex items-center gap-1.5 rounded-md border border-node-style/40 bg-node-style/10 px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:border-node-style disabled:opacity-50">
              {drafting ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
              {drafting ? 'Writing' : 'Write it for me'}
            </button>
            </div>
          </div>
          <textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)}
            className="mt-1.5 w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2.5 text-sm text-white/85 focus:border-node-model focus:outline-none" />
          {findings.length > 0 && (
            <div data-testid="script-findings"
              className={`mt-2 rounded-md border p-2.5 ${findings.some((f) => f.level === 'risk')
                ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
              <div className="flex items-start justify-between gap-2">
                <ul className="min-w-0 space-y-1">
                  {findings.map((f, i) => (
                    <li key={i} className="text-[10px] leading-relaxed text-white/70">
                      <span className={f.level === 'risk' ? 'text-red-300' : 'text-amber-300'}>
                        {f.terms.join(', ')}
                      </span>
                      {' '}{f.why}
                    </li>
                  ))}
                </ul>
                <button onClick={soften} disabled={softening} data-testid="soften-script"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/20 px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:border-white/50 disabled:opacity-50">
                  {softening ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                  {softening ? 'Rewriting' : 'Rewrite it for me'}
                </button>
              </div>
              <p className="mt-1.5 text-[9px] text-white/35">
                Checked before rendering, because a refusal from the provider costs the credits anyway.
              </p>
            </div>
          )}
          {sequenced && clips.length > 0 && (
            <div className="mt-3" data-testid="sequence-plan">
              <div className="flex items-baseline justify-between">
                <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">
                  {clips.length} clips, joined automatically
                </label>
                {edited && (
                  <button onClick={() => setEdited(false)} className="text-[10px] text-white/40 hover:text-white/70">
                    Reset to the automatic split
                  </button>
                )}
              </div>
              <p className="mt-1 text-[10px] text-white/40">
                Each is a different framing of the same shot, joined, with one voice read over
                the whole thing. Nobody speaks on camera in a joined clip, because the voice is
                added afterwards and lips that move would not match it.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <label className="text-[10px] text-white/50">Voice over</label>
                <select value={voVoice} onChange={(e) => setVoVoice(e.target.value)}
                  data-testid="sequence-voice"
                  className="rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2 py-1 text-[11px] text-white/80 focus:border-node-model focus:outline-none">
                  {VO_VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                </select>
                <span className="text-[10px] text-white/35">
                  read in {scriptLang}
                  {scriptLang === 'Spanish' ? ', Castilian rather than Latin American' : ''}
                </span>
              </div>
              <div className="mt-2 space-y-2">
                {clips.map((c, i) => (
                  <div key={i} className="rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[10px] font-medium text-white/70">
                        Clip {i + 1} · {c.starts_at}s to {c.starts_at + c.seconds}s
                      </span>
                      <span className="truncate text-[9px] text-white/35">{c.shot}</span>
                    </div>
                    <textarea rows={2} value={c.script}
                      onChange={(e) => setClipScript(i, e.target.value)}
                      data-testid={`clip-script-${i}`}
                      placeholder="What happens in this clip"
                      className="mt-1.5 w-full resize-none rounded border border-transparent bg-transparent p-1 text-xs text-white/85 focus:border-node-model focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="mt-1 text-[10px] leading-relaxed text-white/40">
            Describe the motion only. The scene compiled from your canvas, the creator&apos;s identity, wardrobe,
            product and styling, is appended automatically so the clip does not drift from the frame.
          </p>
        </div>

        <div className="px-5 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Show something</label>
            {[
              { key: 'none', label: 'Nothing' },
              { key: 'product', label: 'Product in the box' },
              { key: 'website', label: 'Website on screen' },
            ].map((m) => (
              <button key={m.key} data-testid={`reveal-mode-${m.key}`}
                onClick={() => { setRevealMode(m.key); if (m.key === 'none') setReveal(null); }}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${revealMode === m.key
                  ? 'border-node-model bg-node-model/15 text-white'
                  : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>
                {m.label}
              </button>
            ))}
          </div>

          {revealMode === 'product' && (
            <div className="mt-1.5 flex items-center gap-3">
              {reveal ? (
                <div className="relative h-14 w-14 overflow-hidden rounded-lg border border-[#2A2A30]">
                  <img src={reveal} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => setReveal(null)} data-testid="reveal-remove" className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white/80 hover:text-red-400"><Trash2 size={11} /></button>
                </div>
              ) : (
                <button onClick={() => revealRef.current?.click()} data-testid="reveal-upload"
                  className="flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] text-white/40 transition-colors hover:border-node-model hover:text-node-model">
                  <ImagePlus size={16} /><span className="text-[9px]">Add</span>
                </button>
              )}
              <p className="text-[11px] leading-relaxed text-white/45">
                What is inside the box, so the real product is revealed rather than invented.
              </p>
              <input ref={revealRef} type="file" accept="image/*" className="hidden" onChange={onRevealPick} />
            </div>
          )}

          {revealMode === 'website' && (
            <div className="mt-1.5 flex items-center gap-2">
              {reveal && (
                <div className="relative h-14 w-20 shrink-0 overflow-hidden rounded-lg border border-[#2A2A30]">
                  <img src={reveal} alt="" className="h-full w-full object-cover" />
                </div>
              )}
              <input value={revealUrl} onChange={(e) => setRevealUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') grabSite(); }}
                placeholder="casoabierto.com" data-testid="reveal-url"
                className="min-w-0 flex-1 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2.5 py-1.5 text-xs text-white/85 focus:border-node-model focus:outline-none" />
              <button onClick={grabSite} disabled={grabbing || !revealUrl.trim()} data-testid="reveal-grab"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#2A2A30] px-2.5 py-1.5 text-[11px] text-white/80 hover:border-white/40 disabled:opacity-50">
                {grabbing ? <Loader2 size={11} className="animate-spin" /> : <Globe size={11} />}
                {grabbing ? 'Capturing' : reveal ? 'Recapture' : 'Capture'}
              </button>
            </div>
          )}
          {revealMode === 'website' && (
            <p className="mt-1 text-[11px] leading-relaxed text-white/45">
              The page is captured and put on the screen already in the frame, not printed on
              paper. Sending a screenshot down the unboxing path is what put a web page in a box.
            </p>
          )}
        </div>

        {/* The renderer depends on whose key sits first in the chain, so it is read
            rather than written here. Stating fal.ai and 720p as facts was wrong the
            moment a second provider existed. */}
        <div className="flex items-center gap-2 px-5 pb-2 text-[11px] text-white/45">
          <Info size={12} />
          {active?.video?.label ? `Rendered by ${active.video.label}` : 'No video provider configured'}
          {duration > 10 ? ` · joined from ${Math.ceil(duration / 10)} clips with one voice over` : ''}
        </div>

        <div className="flex justify-end gap-2 border-t border-[#2A2A30] px-5 py-3.5">
          <button onClick={onClose} className="rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/70 hover:text-white">Cancel</button>
          <button onClick={() => onGenerate({
            model, duration, aspect, prompt, revealImageDataUrl: reveal,
            // Only sent when they have been touched. Otherwise the backend does
            // the same split itself, and one source of truth is better than two
            // that can drift apart.
            segments: edited ? clips.map((c) => c.script) : null,
            language: scriptLang, voice: voVoice,
            revealMode: revealMode === 'website' ? 'website' : 'product',
          })} disabled={busy} data-testid="video-generate-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Clapperboard size={15} />} Generate video
          </button>
        </div>
      </div>
    </div>
  );
}
